/**
 * audioClipSplit — the single source of truth for splitting an audio clip at a playhead time.
 *
 * RFC-1122 item C: the video editor (timeline `SPLIT_AUDIO_CLIP`) and the unified-audio Lanes
 * editor both need identical playhead-split math. Rather than duplicate it, both call this
 * model-neutral pure helper. It operates on a minimal `ClipWindow` — the clip's placement on the
 * timeline (`windowStartS`..`windowEndS`), where it starts reading its SOURCE asset (`sourceInS`),
 * and its pitch-preserving playback `speed`. Each editor maps its own clip shape to/from this.
 *
 * The two halves tile the source EXACTLY (no gap, no overlap): the first plays
 * `[sourceInS, sourceInS + (split-start)*speed)` and the second continues from there, so a later
 * re-bake reproduces the original audio byte-for-byte with a cut at the playhead.
 */

export interface ClipWindow {
  /** Timeline time (seconds) where the clip starts playing. */
  windowStartS: number
  /** Timeline time (seconds) where the clip stops playing. */
  windowEndS: number
  /** Offset (seconds) into the SOURCE asset the clip starts reading from. */
  sourceInS: number
  /** Pitch-preserving playback rate (1.0 = unity). Source seconds consumed = windowLen * speed. */
  speed: number
}

/**
 * Split a clip window at `splitTimeS` (a timeline time). Returns the two resulting windows, or
 * `null` when the split point is not strictly inside the window (nothing to split).
 *
 * `first`  = `[windowStartS, splitTimeS]` reading from the same `sourceInS`.
 * `second` = `[splitTimeS, windowEndS]` reading from `sourceInS + (splitTimeS - windowStartS) * speed`.
 */
export function splitAudioClipWindow(
  clip: ClipWindow,
  splitTimeS: number,
): { first: ClipWindow; second: ClipWindow } | null {
  // Reject a non-finite split position up front: with NaN, both boundary comparisons below are
  // false, so the helper would otherwise return two windows with NaN timing (which callers could
  // persist through their own NaN-bypassing comparisons). No playhead ⇒ nothing to split.
  if (!Number.isFinite(splitTimeS)) {
    return null
  }
  const relativeTime = splitTimeS - clip.windowStartS
  // Strictly inside: a split at (or outside) an edge produces a zero-length half — reject.
  if (relativeTime <= 0 || relativeTime >= clip.windowEndS - clip.windowStartS) {
    return null
  }
  // Speed-aware: the first half consumes `relativeTime * speed` SOURCE seconds, so the second half
  // starts reading there. Mirrors the video editor's `sourceRelativeTime = relativeTime * speed`.
  const sourceRelativeTime = relativeTime * clip.speed
  const first: ClipWindow = {
    windowStartS: clip.windowStartS,
    windowEndS: splitTimeS,
    sourceInS: clip.sourceInS,
    speed: clip.speed,
  }
  const second: ClipWindow = {
    windowStartS: splitTimeS,
    windowEndS: clip.windowEndS,
    sourceInS: clip.sourceInS + sourceRelativeTime,
    speed: clip.speed,
  }
  return { first, second }
}
